import { AxiosError, AxiosResponse } from "axios";
import {
  all,
  delay,
  put,
  select,
  takeLatest,
  takeLeading,
} from "redux-saga/effects";
import { Urls } from "src/constants";
import { Channel, ChannelProgram } from "src/models";
import { HttpService, PlayerService, StringService } from "src/services";
import { TvAction } from "../actions";
import { ConfigInit, ReduxAction, TvModel } from "../models";
import { TvTypes } from "../types";

export function* TvSaga() {
  yield takeLeading(TvTypes.REQUEST_CHANNEL_LIST, getChannelList);
  yield takeLatest(TvTypes.REQUEST_TV_CONDUCTOR_LIST, getConductorList);
  yield takeLatest(TvTypes.GET_STREAM_URL, getStreamUrl);
  yield takeLatest(TvTypes.SET_TV_CONDUCTOR_CURRENR_DATE, changeDate);
  yield takeLatest(TvTypes.START_TV_PLAYBACK_REPORTING, doReporting);
  yield takeLatest(TvTypes.DO_TV_REPORT_PLAYBACK, reportPlayback);
  yield takeLatest(TvTypes.DO_TV_REPORT_STATISTIC, reportStatistic);
  yield takeLatest(TvTypes.SET_TV_CURRENT_EPISODE, switchEpisode);
}

function* doReporting(action: ReduxAction) {
  yield all([reportPlayback(action), reportStatistic(action)]);
}

function* reportPlayback(action: ReduxAction) {
  try {
    const config: ConfigInit = yield select((state: any) => state.ConfigState);
    yield delay(config.config.appSetting.playReportDuration * 1000);

    const conductor: TvModel = yield select((state: any) => state.TvState);
    const { channelId, conductorId } = action.payload;

    if (!conductor.reporting || conductor.currentChannel?.id !== channelId)
      return;

    const api = Urls.reports.tvPlayBack(channelId, conductorId);
    yield HttpService.Fetch(api);

    yield put(TvAction.reportPlayback(action.payload));
  } catch (ex: any) {
    const error: AxiosError = ex;
    if (!error.response) return;

    if (error.response.status >= 500) {
      yield put(TvAction.reportPlayback(action.payload));
      return;
    }

    const data = error.response.data || {};
    yield PlayerService.setTvAvailabilityByStatus(data, error.response.status);
  }
}

function* reportStatistic(action: ReduxAction) {
  try {
    const config: ConfigInit = yield select((state: any) => state.ConfigState);
    yield delay(config.config.appSetting.statReportDuration * 1000);

    const conductor: TvModel = yield select((state: any) => state.TvState);
    const { channelId, conductorId } = action.payload;

    if (!conductor.reporting || conductor.currentChannel?.id !== channelId)
      return;

    const api = Urls.reports.tvStatistic(channelId, conductorId);
    yield HttpService.Fetch(api);

    yield put(TvAction.reportStatistic(action.payload));
  } catch (ex: any) {
    const error: AxiosError = ex;
    if (!error.response) return;

    if (error.response.status >= 500) {
      yield put(TvAction.reportStatistic(action.payload));
      return;
    }

    const data = error.response.data || {};
    yield PlayerService.setTvAvailabilityByStatus(data, error.response.status);
  }
}

function* getChannelList() {
  try {
    const api = Urls.tv.channels();
    const response: AxiosResponse = yield HttpService.Fetch(api);

    if (response.data) {
      yield put(TvAction.setChannels(response.data));
    }
  } catch {
    yield put(TvAction.hideChannelsLoading());
  }
}

function* changeDate(action: ReduxAction) {
  const conductor: TvModel = yield select((state: any) => state.TvState);

  if (!conductor.currentChannel) return;
  yield put(
    TvAction.requestConductor(conductor.currentChannel, action.payload, "")
  );
}

function* getConductorList(action: ReduxAction) {
  try {
    const { channel, date }: { channel: Channel; date?: Date } = action.payload;

    if (!channel) return;

    const dateString = date
      ? `${date.getFullYear()}/${StringService.toFixed(
          date.getMonth() + 1,
          2
        )}/${StringService.toFixed(date.getDate(), 2)}`
      : "";

    const api = Urls.tv.conductor(channel.id, dateString);
    const response: AxiosResponse = yield HttpService.Fetch(api);

    if (response.data) {
      yield put(TvAction.setConductor(response.data));
    }
  } catch {
    yield put(TvAction.hideConductorLoading());
  }
}

function* getStreamUrl(action: ReduxAction) {
  try {
    const { channelId, conductorId, killSessionId } = action.payload;

    if (!channelId) return;

    const api = conductorId
      ? Urls.tv.episod(channelId, killSessionId, conductorId)
      : Urls.tv.channel(channelId, killSessionId);
    const response: AxiosResponse = yield HttpService.Fetch(api);

    if (response.data) {
      yield put(TvAction.setStreamUrl({ ...response.data, type: "HLS_DVR" }));
    }
  } catch (ex: any) {
    yield put(TvAction.setStreamUrl(undefined));
    const error: AxiosError = ex;
    if (!error.response) return;

    if (error.response.status >= 500) {
      yield put(TvAction.reportStatistic(action.payload));
      return;
    }

    const data = error.response.data || {};
    yield PlayerService.setTvAvailabilityByStatus(data, error.response.status);
  }
}

function* switchEpisode(action: ReduxAction) {
  const episode: ChannelProgram = action.payload;
  const conductor: TvModel = yield select((state: any) => state.TvState);

  if (!conductor.currentChannel) return;

  yield put(TvAction.getStreamUrl(conductor.currentChannel?.id, episode?.id));
}
